import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, authorizedFetch } from "@/lib/api";
import { API_URL, toIntOrNull, toStrOrNull, toUuidOrNull } from "@/lib/mutation-utils";

interface MutationOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

function buildPayload(data: any) {
  return {
    code:         data.code,
    name:         data.name,
    sgtCodes:     Array.isArray(data.sgtCodes) && data.sgtCodes.length > 0 ? data.sgtCodes : null,
    fileId:       toIntOrNull(data.fileId),
    dni:          toStrOrNull(data.dni),
    docTypeId:    toUuidOrNull(data.docTypeId),
    docId:        toStrOrNull(data.docId),
    isOwned:      !!data.isOwned,
    pvcuenta:     toIntOrNull(data.pvcuenta),
    operatorId:   toUuidOrNull(data.operatorId),
    driverStatus: data.driverStatus || undefined,
    phoneCdc:     toStrOrNull(data.phoneCdc),
    phonePersonal: toStrOrNull(data.phonePersonal),
  };
}

export function useDriverMutations(options: MutationOptions = {}) {
  const qc = useQueryClient();

  const createMut = useMutation({
    mutationFn: async (data: any) => {
      const res = await api.providers.drivers.$post({ json: buildPayload(data) });
      // `api` throws on non-2xx; if we got here it's OK.
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"], exact: false });
      toast.success("Chofer creado exitosamente");
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await api.providers.drivers[":id"].$patch({ param: { id }, json: buildPayload(data) });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"], exact: false });
      toast.success("Chofer actualizado exitosamente");
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.providers.drivers[":id"].$delete({ param: { id } });
      // throws on !ok
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"], exact: false });
      toast.success("Chofer eliminado exitosamente");
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await authorizedFetch(`${API_URL}/api/providers/drivers/${id}/restore`, {
        method: "PATCH",
      });
      // `authorizedFetch` throws on !ok
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"], exact: false });
      toast.success("Chofer restaurado exitosamente");
      options.onSuccess?.();
    },
    onError: (err: Error) => options.onError?.(err),
  });

  return {
    createMut,
    updateMut,
    deleteMut,
    restoreMut,
    isSaving: createMut.isPending || updateMut.isPending,
  };
}
